

// We need to encode 26 letters plus null. That fits in 4.75 bits. There's
// probably some clever way to use the 0.25 remaining bits, but I don't know
// it. Instead 26-30 are used to encode repeating null values and 31 is used
// as a delimiter.
// 26 - 1 null
// 27 - 2 null
// 28 - 4 null
// 29 - 8 null
// 30 - 16 null

import Game from "./Game";
import { PlayEdit } from "./PlayEditToggle";
import { rc2s, s2rc } from "./rc";

const VERSION = "1";

enum indices {
  A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, T, U, V, W, X, Y, Z,
  null_1, null_2, null_4, null_8, null_16,
  stop
}

export const chars = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z"] as const;
export type char = ""|typeof chars[number];

export const numbers = ["1","2","3","4","5","6","7","8","9","10","11","12","13","14","15","16","17","18","19","20","21","22","23","24","25","26"] as const;
export type num = typeof numbers[number];

export function asChar(letter : string) : char|undefined {
  if (letter.length > 1 || letter < "A" || letter > "Z") return undefined;
  return letter as char;
}

//  |. . . . . . . .|. . . . . . . .|. . . . . . . .|. . . . . . . .|. . . . . . . .|
//  |- - - - -|- - - - -|- - - - -|- - - - -|- - - - -|- - - - -|- - - - -|- - - - -|
function serializer() {
  let output = "";

  let buffer = 0;
  let bufferBits = 0;

  // write the `bits` LSBs from `value` to output
  const put = (bits : number, value : number) => {
    const shift = 8 - bufferBits - bits;
    value &= 2 ** bits - 1;

    if (shift > 0) {
      buffer += value << shift;
      bufferBits += bits;
    } else {
      const highBits = value >> -shift;
      output += String.fromCharCode(buffer + highBits);

      buffer = bufferBits = 0;
      put(-shift, value);
    }
  };

  // flush any buffered bits to output
  const flush = () => {
    if (bufferBits !== 0) put(8 - bufferBits, 0x00);
  };

  // write a uint5 to output
  const putUint5 = put.bind(null, 5);

  // write a uint8 to output
  const putUint8 = put.bind(null, 8);

  // write a uint1 to output
  const putUint1 = put.bind(null, 1);

  // write `n` nulls to output
  const putNulls = (n : number) => {
    while (n >= 16) {
      putUint5(indices.null_16);
      n -= 16;
    }

    if (n >= 8) {
      putUint5(indices.null_8);
      n -= 8;
    }

    if (n >= 4) {
      putUint5(indices.null_4);
      n -= 4;
    }

    if (n >= 2) {
      putUint5(indices.null_2);
      n -= 2;
    }

    if (n === 1) {
      putUint5(indices.null_1);
    }
  };

  return {
    writeChars: (data : char[]) : void => {
      let nulls = 0;
      data.forEach(c => {
        if (c === "") {
          nulls++;
          return;
        }

        if (nulls > 0) {
          putNulls(nulls);
          nulls = 0;
        }

        putUint5(indices[c]);
      });

      if (nulls > 0) putNulls(nulls);
      putUint5(indices.stop);
    },
    writeUint8: (data : number) : void => {
      putUint8(data);
    },
    writeBoolean: (data: boolean) : void => {
      putUint1(Number(data));
    },
    final: () => {
      flush();
      return output;
    }
  }
}

//  |. . . . . . . .|. . . . . . . .|. . . . . . . .|. . . . . . . .|. . . . . . . .|
//  |- - - - -|- - - - -|- - - - -|- - - - -|- - - - -|- - - - -|- - - - -|- - - - -|
function deserializer(raw : string) {
  let offset = 0;
  let buffer = 0;
  let bufferBits = 0;

  const EOF = "EOF";

  const readBits = (n : number) : number => {
    if (bufferBits === 0) {
      if (offset === raw.length) throw EOF;
      buffer = raw.charCodeAt(offset++);
      bufferBits = 8;
    }

    let ret : number;
    const shift = n - bufferBits;

    if (shift > 0) {
      bufferBits = 0;
      ret = (buffer << shift) + readBits(shift);;
    } else {
      bufferBits -= n;
      ret = buffer >> -shift;
    }

    return ret & (2 ** n - 1);
  }

  const readChar = () : char[] => {
    let i : number;

    try {
      i = readBits(5);
    } catch(e) {
      if (e === EOF) return[];
      throw e;
    }

    switch (i) {
      case indices.null_1:
        return Array(1).fill("");
      case indices.null_2:
        return Array(2).fill("");
      case indices.null_4:
        return Array(4).fill("");
      case indices.null_8:
        return Array(8).fill("");
      case indices.null_16:
        return Array(16).fill("");
      case indices.stop:
        return [];
      default:
        return [indices[i] as char];
    }
  }

  return {
    readChars: () : char[] => {
      let ret : char[] = [];
      for (let chunk = readChar(); chunk.length; chunk = readChar()) {
        ret.push(...chunk);
      }
      return ret;
    },
    readUint8: () : number => {
      return readBits(8);
    },
    readBoolean: () : boolean => {
      const b = readBits(1);
      return b === 1;
    },
  };
}

interface historyState {
  isEdit : boolean,
  stateWithVersion : string,
};

export function dumpState(g : Game) : historyState {
  const m = g.matrix;
  if (m === null) throw new Error("game has no matrix");

  const {writeChars, writeUint8, writeBoolean, final} = serializer();

  let matrixDataFlat : char[] = [];
  for (let r = m.minRow; r < m.maxRow; r++) {
    for (let c = m.minCol; c < m.maxCol; c++) {
      matrixDataFlat.push(g.state.matrixData[rc2s(r, c)] || "");
    }
  }
  writeChars(matrixDataFlat);

  // using fixed length encoding, since we know it's 26 chars
  const solutionDataFlat = Object.values(g.state.solutionData);
  writeChars(solutionDataFlat);

  // account for matrices not starting at 0,0 by offset by min row/col
  const {row : fRow, col : fCol} = s2rc(m.state.focussed);
  writeUint8(fRow - m.minRow);
  writeUint8(fCol - m.minCol);

  const isEdit = g.state.playEdit === PlayEdit.Edit;
  writeBoolean(false); // used to be isEdit

  const stateWithVersion = VERSION + window.btoa(final()).replace(/=/g, "");

  return {
    isEdit,
    stateWithVersion
  };
}

export function loadState(g : Game, {isEdit, stateWithVersion} : historyState) {
  const m = g.matrix;
  if (m === null) return;

  const [version, b64State] = [stateWithVersion[0], stateWithVersion.slice(1)];
  if (version !== VERSION) throw new Error("unknown version")

  const rawState = window.atob(b64State);

  const {readChars, readUint8, readBoolean} = deserializer(rawState);

  const matrixDataFlat = readChars();
  const mdSize = Math.sqrt(matrixDataFlat.length);
  let matrixData : Record<string, char> = {};
  for (let row = 0; row < mdSize; row++) {
    for (let col = 0; col < mdSize; col++) {
      matrixData[rc2s(row, col)] = matrixDataFlat[row * mdSize + col];
    }
  }

  const solutionDataFlat = readChars();
  const solutionData = Object.fromEntries(
    numbers.map((n, i) => [n, solutionDataFlat[i]])
  );

  const [fRow, fCol] = [readUint8(), readUint8()];
  const focussed = rc2s(fRow, fCol);

  readBoolean(); // used to be isEdit
  const playEdit = isEdit ? PlayEdit.Edit : PlayEdit.Play;

  g.setState({matrixData, solutionData, playEdit});
  m.focusElt(focussed);
}